import { createContext, useContext } from 'react'
import type { CharterRewardLegResult } from 'hooks/Flights/useCharterRewardAvailability'
import type { FlightsClassTypes } from 'constants/flight'

/**
 * Per-cabin pair of charter journeys for a single calendar date (keyed by `checkIn`).
 * `departing.flightGroup.flights.at(-1).arrivalDate === checkIn` and
 * `returning.flightGroup.flights.at(0).departingDate === checkOut` for the same date row.
 */
export interface CabinDateJourneys {
  departing: App.JourneyV2 | undefined
  returning: App.JourneyV2 | undefined
}

export interface CharterRewardAvailabilityContextValue {
  isLoading: boolean
  /** True only when every leg listed for the date in the provider has `available === true`. */
  isAvailableForDate: (dateKey: string) => boolean
  /** Per-leg lookup so cells can read points + cash co-pay per fare class without re-firing requests. */
  getLegResult: (journeyKey: string) => CharterRewardLegResult | undefined
  /**
   * Per-cabin per-date journey lookup so trip-options-table cells can render cabin-correct
   * cash prices without each row firing its own `useAllCharterFlightJourneys`. The shell-level
   * fetcher hydrates this once per cash cabin and the context replays the result.
   */
  getJourneysForCabinDate: (
    cabin: FlightsClassTypes,
    dateKey: string,
  ) => CabinDateJourneys | undefined
}

const NOOP: CharterRewardAvailabilityContextValue = {
  isLoading: false,
  isAvailableForDate: () => false,
  getLegResult: () => undefined,
  getJourneysForCabinDate: () => undefined,
}

/**
 * Surface-scoped Classic Rewards availability lookup. The calendar shell fetches
 * `/rewards/availability` once for every visible leg and exposes a per-date check via this
 * context; descendant cells/rows read it without firing their own requests.
 */
export const CharterRewardAvailabilityContext =
  createContext<CharterRewardAvailabilityContextValue>(NOOP)

export function useCharterRewardAvailabilityContext(): CharterRewardAvailabilityContextValue {
  return useContext(CharterRewardAvailabilityContext)
}
